import React, { useState, useEffect, useRef, useCallback } from 'react';
import className from 'classnames/bind';
import { connect } from 'react-redux';
import ReviewAllShimmer from '../../shimmers/reviewAll.shimmer';
import Dialog from '../../molecules/Dialog';
import Icon from '../../atoms/Icon';
import { getAllReviews } from '../../../actions/ratings';
import ReviewDetails from './ReviewDetails';
import ProductRating from '../ProductRating';
import ReviewSinglePhotoCard from './ReviewSinglePhotoModal/ReviewSinglePhotoCard';
import CustomerPhotoGridModal from './ReviewCustomerPhotoGridModal/CustomerPhotoGridModal';
import { SortByReviews } from './constants';
import properties from '../../../modules/ajio-util/properties';
import { gtmEventPush } from '../../../modules/ga/GTMdatalayer';
import { getCurrentStore } from '../../selectors/metaData';
import { FLEEK_STORE } from '../../../constants/constant';
import styles from './allReviewsMobile.pcss';
import { replaceWithJioCDN } from '../../../utils/imageHandling';
import { getRatingParityMsiteFlag } from '../../selectors/featureFlagsSelector';

const cx = className.bind(styles);
const ViewAllReviewsMobile = (props: any) => {
  const {
    customerWords,
    imageReview,
    productCode,
    productName,
    product_id,
    product_brick,
    featureData,
    mandatoryInfo,
    luxe,
    isSneakerHoodOn,
    sneakerHoodFeatureDataList,
    productBrand,
    getAggregateRating,
    getSubRatings,
    imageUrl,
    isSignedIn,
    showModal,
    hideModal,
    currentStore = '',
    ratingParityMsite = false
  } = props;
  const pageSize = 20;
  const isFleek = currentStore === FLEEK_STORE;
  const [textReviews, setTextReview] = useState([]);
  const [viewPhotoReviews, setPhotoReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [textLoading, setTextLoading] = useState(false);
  const [showSortBy, setShowSortBy] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [showHalfCard, setShowHalfCard] = useState(false);
  const [showHalfCardSingle, setShowHalfCardSingle] = useState(false);
  const targetRef = useRef(null);
  const inputRef = useRef(null);
  const apiCalledRef = useRef(false);
  const { getAllReviews } = props;
  const photosArrayToShow =
    imageReview?.totalReviews > 5
      ? imageReview?.productReviewImages.slice(0, 5)
      : imageReview?.productReviewImages;
  const textNumberOnPhotos = (imageReview?.totalReviews > 5 && imageReview?.totalReviews - 5) || 0;

  const [filters, setFilters] = useState({
    reviewWithPhotos: false,
    verifiedPurchase: false,
    sortBy: null
  });
  const [rating, setRating] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [photoPageNumber, setphotoPageNumber] = useState(1);

  const productDetails = {
    productId: product_id ?? '',
    productName: productName ?? '',
    productBrick: product_brick ?? ''
  };

  const handleRatingChange = event => {
    const value = parseInt(event.target.value);
    const checked = event.target.checked;
    handleCheckboxRating(value, checked);
  };

  const handleCheckboxRating = (value, checked) => {
    if (checked) {
      setRating([...rating, value]);
    } else {
      setRating(rating.filter(item => item !== value));
    }
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  const handleFilterChange = (event: any) => {
    const { name, type, value, checked } = event.target;
    setFilters(prevFilters => ({
      ...prevFilters,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (type === 'radio') {
      applyFilters(value);
    }
  };

  const resetFilters = () => {
    setFilters({
      reviewWithPhotos: false,
      verifiedPurchase: false,
      sortBy: null
    });
    setRating([]);
    setPageNumber(1);
  };

  const fetchPhotoData = async pageNum => {
    try {
      const filters = {
        rating: [],
        reviewWithPhotos: true,
        verifiedPurchase: false,
        sortBy: SortByReviews[0].value,
        pageNumber: pageNum,
        skuId: product_id
      };
      setLoading(true);
      const newData = await getAllReviews(filters);
      const { productReviews } = newData.reviewData;
      if (productReviews && productReviews.length > 0 && productReviews.length === pageSize) {
        setphotoPageNumber(pageNum);
      }
      setPhotoReviews(prevData => [...prevData, ...productReviews]);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const clickMorePhotos = () => {
    setShowHalfCard(true);
    fetchPhotoData(1);
    setPhotoReviews([]);
    gtmEventPush({
      event: 'reviews_rating_interactions',
      event_category: 'reviews_rating_interactions',
      event_action: 'customer photos',
      event_label: 'clicked',
      ...(product_id.length > 0 && { product_id }),
      ...(product_brick.length > 0 && { product_brick }),
      ...(productName.length > 0 && { product_name: productName }),
      pageType: 'PDP',
      store_type: 'ajio'
    });
  };

  const fetchData = async filters => {
    try {
      setTextLoading(true);
      const newData = await getAllReviews(filters);
      const { productReviews } = newData.reviewData;
      if (productReviews && productReviews.length > 0 && productReviews.length === pageSize) {
        setPageNumber(pageNumber + 1);
      }
      if (productReviews && productReviews.length > 0) {
        setTextReview(prevData => [...prevData, ...productReviews]);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
      setTextLoading(false);
    }
  };

  const applyFilters = value => {
    const reqObj = {
      ...filters,
      rating,
      pageNumber: 1,
      pageSize,
      skuId: product_id
    };
    if (value) {
      reqObj.sortBy = value;
    }
    setPageNumber(1);
    setTextReview([]);
    fetchData(reqObj);
    showFilterModal(false);
    showSortModal(false);
  };

  useEffect(() => {
    // add event listener to the window object to prevent scrolling of the page behind the modal
    const body = document.body;
    const scrollY = window.scrollY;
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.width = '100%';

    // remove event listener when the modal is closed
    return () => {
      body.style.position = '';
      body.style.top = '';
      body.style.width = '';
      window.scrollTo(0, scrollY);
    };
  }, []);

  useEffect(() => {
    const currentRef = targetRef.current;
    if (!currentRef) return;

    const observer = new IntersectionObserver(
      entries => {
        const target = entries[0];
        if (target.isIntersecting && !apiCalledRef.current) {
          apiCalledRef.current = true;
          callApi();
        } else if (!target.isIntersecting) {
          apiCalledRef.current = false;
        }
      },
      { threshold: 0.5 }
    );

    observer.observe(currentRef);

    // cleanup function to disconnect the observer
    return () => {
      observer.disconnect();
    };
  }, []);

  const callApi = () => {
    // make API call here
    let pageNum = pageNumber;
    if (textReviews && textReviews.length !== 0) {
      pageNum = pageNumber + 1;
    }
    const apiFilters = {
      ...filters,
      pageNumber: pageNum,
      pageSize,
      skuId: product_id
    };
    if (textReviews && textReviews.length % pageSize === 0) {
      fetchData(apiFilters);
    }
  };

  const showSortModal = value => {
    setShowSortBy(value);
  };

  const showFilterModal = value => {
    setShowFilter(value);
  };

  const showHalfCardSingleClick = () => {
    setShowHalfCard(false);
    setShowHalfCardSingle(true);
  };

  const renderSinglePhotoModal = (elObj, i) => {
    showModal('reviews-single-photo', {
      state: {
        reviewsCollection: [],
        reviewData: elObj,
        loop: true,
        arrowEnabled: false,
        clickIndex: i
      }
    });
    setShowHalfCardSingle(true);
  };

  const gaEventOnCross = () => {
    gtmEventPush({
      event: 'reviews_rating_interactions',
      event_category: 'reviews_rating_interactions',
      event_action: 'photo interaction',
      event_label: 'cross',
      ...(product_id.length > 0 && { product_id }),
      ...(product_brick.length > 0 && { product_brick }),
      ...(productName.length > 0 && { product_name: productName }),
      pageType: 'PDP',
      store_type: 'ajio'
    });
  };

  return (
    <div className={cx('modal', isFleek && 'fleek-modal')}>
      <button
        className={cx('back-btn', isFleek ? 'back-btn-fleek' : '')}
        onClick={props.closeModal}
      >
        <img
          alt="Back arrow"
          className={cx('backIcon-modal')}
          src={`${properties.getProperty('imageBaseUrl')}${
            isFleek ? '/static/img/fleek-back-arrow.svg' : '/static/img/back-arrow-refresh.svg'
          }`}
        />
      </button>
      <div className={cx('modal-content', isFleek && 'fleek-modal-content')}>
        <div className={cx('rating-card-withReviewAll')}>
          <div className={cx('rating-card')}>
            <ProductRating
              featureData={featureData}
              mandatoryInfo={mandatoryInfo}
              luxe={luxe}
              isSneakerHoodOn={isSneakerHoodOn}
              sneakerHoodFeatureDataList={sneakerHoodFeatureDataList}
              productCode={productCode}
              productName={productName}
              productBrand={productBrand}
              product_id={productCode}
              product_brick={product_brick}
              getAggregateRating={getAggregateRating}
              getSubRatings={getSubRatings}
              imageUrl={imageUrl}
              customerWords={customerWords}
              isSignedIn={isSignedIn}
              isFleek={isFleek}
            />
          </div>
        </div>
        <div className={cx('separator', isFleek && 'separator-fleek')} />
        <div className={cx('photo-all-review')}>
          {imageReview?.totalReviews > 0 && (
            <div className={cx('reviews-photos-section')}>
              <span
                className={cx('reviews-section-title', isFleek && 'reviews-section-fleek-title')}
                role='heading'
                aria-level={3}
              >
                <strong>Customer Photos</strong>
              </span>
              <div className={cx('reviews-section-customer-photos')} role="list" style={{display: 'flex'}}>
                {photosArrayToShow.length > 0 &&
                  photosArrayToShow.map((el1, i) => {
                    const imageUrlContent = el1?.imageMap?.thumbnail;
                    const imgUrl = imageUrlContent?.length > 0 && imageUrlContent[0].imageUrl;
                    if (textNumberOnPhotos > 0 && i === 4) {
                      return (
                        <div role="listitem">
                        <div className={cx('reviews-section-image')} aria-haspopup="dialog" role="button" tabIndex={0} onClick={clickMorePhotos}>
                          <img
                            className={cx('reviews-section-img', 'blur-bg')}
                            src={`${replaceWithJioCDN(imgUrl)}`}
                            alt="Customer review image"
                          />
                          <div className={cx('reviews-section-textimage')}>
                            + {textNumberOnPhotos}
                          </div>
                        </div>
                        </div>
                      );
                    }
                    return (
                      <div role="listitem">
                      <div className={cx('reviews-section-image')} aria-haspopup="dialog" role="button" tabIndex={0} onClick={clickMorePhotos}>
                        <img
                          className={cx('reviews-section-img')}
                          src={`${replaceWithJioCDN(imgUrl)}`}
                          alt="Customer review image"
                        />
                      </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
          <div className={cx('reviews-review-section')}>
            <div className={cx('reviews-header')}>
              <span
                className={cx('reviews-section-title', isFleek && 'reviews-section-fleek-title')}
                role='heading'
                aria-level={3}
              >
                Reviews
              </span>
            </div>
            <div ref={targetRef} className={cx('review-box')}>
              <>
                {textReviews?.length > 0 &&
                  textReviews.map(el => {
                    return (
                      <ReviewDetails
                        renderSinglePhotoModal={renderSinglePhotoModal}
                        key={el.id}
                        isDesktop={false}
                        reviewData={el}
                        productDetails={productDetails}
                        isFleek={isFleek}
                        ratingParityMsite={ratingParityMsite}
                      />
                    );
                  })}
                {!textLoading && textReviews.length === 0 && (
                  <span className={cx('no-reviews')}>
                    No reviews to display for the filters applied
                  </span>
                )}
              </>
              {textLoading && <ReviewAllShimmer isFleek={isFleek} />}
            </div>
          </div>
        </div>
      </div>
      <div className={cx('reviews-filter-section')}>
        <div onClick={() => showSortModal(true)} className={cx('sort-by')}>
          <Icon
            icon="icon-Sort"
            styles={{ fontSize: '1.6rem', fontWeight: 'normal', marginRight: '5px' }}
            ariaLabel="sort by"
          />
          SORT BY
        </div>
        <hr className={cx('review-separator')} />
        <div onClick={() => showFilterModal(true)} className={cx('other-filter')}>
          <Icon
            icon="icon-Filter"
            styles={{ fontSize: '1.6rem', fontWeight: 'normal', marginRight: '5px' }}
            ariaLabel="filters"
          />
          FILTERS
          {(filters.reviewWithPhotos || filters.verifiedPurchase || rating.length > 0) && (
            <span className={cx('circle')} />
          )}
        </div>
      </div>
      {showSortBy && (
        <Dialog
          showDialog={showSortBy}
          type="drawer"
          onCancel={() => showSortModal(false)}
          customContentClass={isFleek ? 'fleek-pdp-dialog' : ''}
        >
          <div className={cx('modal-sort')}>
            <span className={cx('heading', isFleek && 'fleek-heading')}>Sort By</span>
            {SortByReviews.map((item, index) => (
              <label key={index} className={cx('radio-label')}>
                <input
                  type="radio"
                  name="sortBy"
                  value={item.value}
                  checked={filters.sortBy === item.value}
                  onChange={handleFilterChange}
                  style={{ display: 'none' }}
                />
                <img
                  alt={
                    filters.sortBy === item.value
                      ? 'Radio button selected'
                      : 'Radio button unselected'
                  }
                  style={{ marginRight: '12px' }}
                  src={`${properties.getProperty('imageBaseUrl')}/static/img/${
                    filters.sortBy === item.value
                      ? isFleek
                        ? 'fleekRadioChecked.svg'
                        : 'radioChecked.svg'
                      : isFleek
                      ? 'fleekRadioUnchecked.svg'
                      : 'radioUnChecked.svg'
                  }`}
                />
                {item.name}
              </label>
            ))}
          </div>
        </Dialog>
      )}
      {showFilter && (
        <Dialog
          showDialog={showFilter}
          type="drawer"
          onCancel={() => showFilterModal(false)}
          customContentClass={isFleek ? 'fleek-pdp-dialog' : ''}
        >
          <div className={cx('filters')}>
            <div className={cx('heading', isFleek && 'fleek-heading')}>Filter</div>
            <div className={cx('star-rating')}>
              {[...Array(5)].reverse().map((_, index) => (
                <a
                  onClick={() => {
                    const ratingValue = 5 - index;
                    if (rating.includes(ratingValue)) {
                      setRating(rating.filter(value => value !== ratingValue));
                    } else {
                      setRating([...rating, ratingValue]);
                    }
                  }}
                  className={
                    rating.includes(5 - index)
                      ? cx('star-box-checkedStar', isFleek && 'star-box-fleek-checkedStar')
                      : cx('star-box', isFleek && 'star-box-fleek')
                  }
                >
                  <label key={index}>
                    {' '}
                    {5 - index}
                    {/* <input
                      type="checkbox"
                      name="rating"
                      value={5 - index}
                      checked={rating.includes(5 - index)}
                    /> */}
                  </label>
                  <span className={cx('star-icon')}>
                    <img
                      alt="Star selected"
                      className={cx('star-icon-img')}
                      src={`${properties.getProperty('imageBaseUrl')}/static/img/starRating.svg`}
                    />
                  </span>
                </a>
              ))}
            </div>
            <div className={cx('filters-review-checkbox')}>
              <label
                className={cx('review-label', isFleek && 'review-fleek-label')}
                key="review-with-photos"
              >
                <input
                  type="checkbox"
                  name="reviewWithPhotos"
                  id="reviewWithPhotos"
                  checked={filters.reviewWithPhotos}
                  style={{ display: 'none' }}
                  onChange={handleFilterChange}
                />
                <img
                  alt={filters.reviewWithPhotos ? 'Checkbox selected' : 'Checkbox unselected'}
                  style={{ marginRight: '12px' }}
                  src={`${properties.getProperty('imageBaseUrl')}/static/img/${
                    filters.reviewWithPhotos
                      ? isFleek
                        ? 'fleekCheckedBox.svg'
                        : 'checkedBox.svg'
                      : isFleek
                      ? 'fleekUncheckedBox.svg'
                      : 'unCheckedBox.svg'
                  }`}
                />
                Reviews with photos
              </label>
            </div>
            <div className={cx('filters-review-checkbox')}>
              <label
                className={cx('review-label', isFleek && 'review-fleek-label')}
                key="verified-purchase"
              >
                <input
                  type="checkbox"
                  name="verifiedPurchase"
                  checked={filters.verifiedPurchase}
                  style={{ display: 'none' }}
                  onChange={handleFilterChange}
                />
                <img
                  alt={filters.verifiedPurchase ? 'Checkbox selected' : 'Checkbox unselected'}
                  style={{ marginRight: '12px' }}
                  src={`${properties.getProperty('imageBaseUrl')}/static/img/${
                    filters.verifiedPurchase
                      ? isFleek
                        ? 'fleekCheckedBox.svg'
                        : 'checkedBox.svg'
                      : isFleek
                      ? 'fleekUncheckedBox.svg'
                      : 'unCheckedBox.svg'
                  }`}
                />
                Verified purchase
              </label>
            </div>
            <div className={cx('reset-apply')}>
              <button
                style={
                    isFleek
                    ? { background: '#060C10', color: '#EBFF00', border: '1px solid #EBFF00' }
                      : {}
                  }
className={cx('reset-btn')}
onClick={resetFilters}>
                Reset
              </button>
              <button
                style={
                    isFleek
                      ? { background: '#EBFF00', color: '#060C10', border: '1px solid #EBFF00' }
                    : {}
                }
                className={cx('apply-btn')}
onClick={() => applyFilters(null)}>
                Apply Filter
              </button>
            </div>
          </div>
        </Dialog>
      )}
      <Dialog
        showDialog={showHalfCard}
        type="drawer"
        customContentWrapperClass={isFleek ? 'reviews-fleek' : 'reviews'}
        customContentClass={isFleek ? 'fleek-reviews-dialog' : ''}
        onCancel={() => {
          setShowHalfCard(false);
        }}
      >
        <CustomerPhotoGridModal
          app="MSITE"
          pageSize={pageSize}
          pageNumber={photoPageNumber}
          fetchData={fetchPhotoData}
          loading={loading}
          viewPhotoReviews={viewPhotoReviews}
          showHalfCardSingleClick={showHalfCardSingleClick}
          isFleek={isFleek}
          productDetails={productDetails}
        />
      </Dialog>
      <Dialog
        showDialog={showHalfCardSingle}
        customClass="review-msite-od"
        customContentWrapperClass={isFleek ? 'reviews-fleek' : 'reviews'}
        customContentClass={isFleek ? 'fleek-reviews-dialog' : ''}
        type="drawer"
        onCancel={() => {
          gaEventOnCross();
          setShowHalfCardSingle(false);
          hideModal('reviews-single-photo');
        }}
      >
        <ReviewSinglePhotoCard isFleek={isFleek} />
      </Dialog>
    </div>
  );
};

const mapDispatchToProps = dispatch => {
  return {
    getAllReviews: (reqObj = {}) => dispatch(getAllReviews(reqObj))
  };
};
const mapStateToProps = state => ({
  currentStore: getCurrentStore(state),
  ratingParityMsite: getRatingParityMsiteFlag(state)
});
const connector = connect(mapStateToProps, mapDispatchToProps);

export default connector(ViewAllReviewsMobile);
